import { Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

export interface Step {
  id: string;
  label: string;
}

/**
 * A calm step-progress line for work in flight: the steps done (check), the current one (a quiet
 * spinner ring that stays still under reduced motion), then the outcome. One polite live region
 * announces the current step. Its state comes from props, so a caller that keeps its job state
 * across navigation shows the same line on return.
 */
export function StepProgress(props: {
  steps: readonly Step[];
  /** Index of the step in progress; `steps.length` once every step is done. */
  current: number;
  outcome?: "done" | "failed" | null;
  label: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const { steps, current, outcome } = props;
  const active = steps[Math.min(current, steps.length - 1)];
  const announcement =
    outcome === "done"
      ? t("progress.done")
      : outcome === "failed"
        ? `${t("progress.failed")}: ${active?.label ?? ""}`
        : active
          ? t("progress.stepOf", { current: current + 1, total: steps.length, name: active.label })
          : "";
  return (
    <div className={cn("flex flex-col gap-2", props.className)}>
      <ol
        aria-label={props.label}
        className="flex flex-col items-start gap-x-2 gap-y-1.5 text-xs sm:flex-row sm:flex-wrap sm:items-center"
      >
        {steps.map((step, i) => {
          const state =
            i < current || outcome === "done"
              ? "done"
              : i === current
                ? outcome === "failed"
                  ? "failed"
                  : "current"
                : "todo";
          return (
            <li
              key={step.id}
              aria-current={state === "current" ? "step" : undefined}
              className="flex items-center gap-2 whitespace-nowrap"
            >
              {/* The connector leads its step; phones stack the steps and drop it. */}
              {i > 0 ? (
                <span aria-hidden="true" className="hidden h-px w-4 bg-line-strong/60 sm:block" />
              ) : null}
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 font-medium",
                  state === "done" && "text-fg-secondary",
                  state === "current" && "text-fg",
                  state === "failed" && "text-danger-fg",
                  state === "todo" && "text-fg-muted",
                )}
              >
                <StepMark state={state} />
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </div>
  );
}

function StepMark({ state }: { state: "done" | "current" | "failed" | "todo" }) {
  if (state === "done") {
    return (
      <span className="grid size-4 place-items-center rounded-full bg-surface-selected text-fg-secondary">
        <Check aria-hidden="true" className="size-3" strokeWidth={2.5} />
      </span>
    );
  }
  if (state === "failed") {
    return (
      <span className="grid size-4 place-items-center rounded-full bg-danger-bg text-danger-fg">
        <X aria-hidden="true" className="size-3" strokeWidth={2.5} />
      </span>
    );
  }
  if (state === "current") {
    return (
      <span
        aria-hidden="true"
        className="size-4 rounded-full border-2 border-line border-t-fg-secondary motion-safe:animate-spin"
      />
    );
  }
  return <span aria-hidden="true" className="size-4 rounded-full border border-line-strong" />;
}
